import {
  LoaderCircleIcon,
  SearchIcon,
  User as UserIcon,
  Users2,
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import Typeahead from 'react-bootstrap-typeahead/types/core/Typeahead';
import { BsChevronRight } from 'react-icons/bs';
import { useNavigate } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { MEDIA_URL } from '@/config';
import { api } from '@/lib/api-client';
import { Team, User } from '@/types';

interface SearchData {
  teams: Team[];
  players: User[];
}

const MainSearch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<SearchData>({
    teams: [],
    players: [],
  });
  const navigate = useNavigate();
  const searchRef = useRef<Typeahead>(null);

  const handleSearch = (query: string) => {
    setIsLoading(true);

    api.get(`/search/main?query=${query}`).then((response: any) => {
      setOptions(response);
      setIsLoading(false);
    });
  };

  const filterBy = () => true;

  const labelKey: any = (option: any) => {
    if ('team_name' in option) {
      return option.team_name;
    } else if ('username' in option) {
      return option.username;
    }
    return '';
  };

  return (
    <AsyncTypeahead
      id="mainsearch"
      filterBy={filterBy}
      renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
        <div className="relative">
          <Input
            {...inputProps}
            className="peer h-9 ps-9"
            placeholder="Search..."
            type="search"
            ref={(node) => {
              inputRef(node);
              referenceElementRef(node);
            }}
          />
          <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
            {isLoading ? (
              <LoaderCircleIcon
                className="animate-spin"
                size={16}
                role="status"
                aria-label="Loading..."
              />
            ) : (
              <SearchIcon size={16} aria-hidden="true" />
            )}
          </div>
        </div>
      )}
      className="w-full"
      isLoading={isLoading}
      labelKey={labelKey}
      minLength={2}
      ref={searchRef}
      onSearch={handleSearch}
      options={[...options.teams, ...options.players]}
      renderMenu={(results: any[], menuProps) => {
        // Check if results are available or loading
        return (
          <Menu
            {...menuProps}
            className="z-50 mt-2 flex! w-full flex-col rounded-lg border border-border bg-popover p-2 shadow-sm"
            emptyLabel={
              <div className="flex items-center gap-x-3.5 px-3 py-2 text-sm text-muted-foreground">
                {isLoading && results.length === 0 ? (
                  <Spinner size="sm" />
                ) : (
                  'Nothing found, try searching again.'
                )}
              </div>
            }
          >
            {results.map((result: any, index) => {
              // Render team or player item based on type
              const isTeam = 'team_name' in result;
              const label = isTeam ? result.team_name : result.username;
              const image = isTeam
                ? `${MEDIA_URL}/images/uploads/${result.team_photo}`
                : result.profile_photo?.includes('https')
                ? result.profile_photo
                : `${MEDIA_URL}/${result.profile_photo}`;

              // Check if the current item is the first player after teams
              const isFirstPlayerAfterTeams =
                !isTeam && index > 0 && 'team_name' in results[index - 1];

              // Check if the current item is the first team
              const isFirstTeam =
                isTeam && (index === 0 || !('team_name' in results[index - 1]));

              // Check if there are no teams in the results
              const noTeams = results.every(
                (result) => !('team_name' in result),
              );

              // Determine if this is the first player and there are no teams
              const isFirstPlayerWithNoTeams = index == 0 && noTeams;

              return (
                <React.Fragment key={index}>
                  {(isFirstPlayerAfterTeams || isFirstPlayerWithNoTeams) && (
                    <div className="mt-2 border-t border-border pt-2 first:mt-0 first:border-t-0 first:pt-0">
                      <span className="flex items-center gap-x-2 px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
                        Players
                      </span>
                    </div>
                  )}
                  {isFirstTeam && (
                    <div className="">
                      <span className="flex items-center gap-x-2 px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
                        Teams
                      </span>
                    </div>
                  )}
                  <MenuItem
                    key={index}
                    className={`flex items-center gap-x-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted focus:ring-2 focus:ring-primary`}
                    onClick={() => {
                      navigate(
                        isTeam ? `/team/${result.id}` : `/profile/${result.id}`,
                      );
                      if (searchRef.current) {
                        searchRef.current.clear();
                        searchRef.current.blur();
                      }
                    }}
                    option={result}
                    position={index}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={image} alt={label || ''} />
                      <AvatarFallback>
                        {isTeam ? <Users2 size={16} /> : <UserIcon size={16} />}
                      </AvatarFallback>
                    </Avatar>
                    <div className="mx-2 truncate" title={label}>
                      {label}
                    </div>
                    <span className="text-blue ml-auto">
                      <BsChevronRight />
                    </span>
                  </MenuItem>
                </React.Fragment>
              );
            })}
          </Menu>
        );
      }}
    />
  );
};

export default MainSearch;
